import { useEffect, type ReactNode } from 'react'
import { motion } from 'motion/react'
import FallingLogos from '@/components/FallingLogos'
import { CtaButton } from '@/components/home/CtaButton'
import { BODY_FONT, EASE_OUT } from '@/lib/constants'
import { COLORS } from '@/components/home/tokens'
import { setErrorPage } from '@/lib/errorPageState'

export interface ErrorPageProps {
  /** Large status code or label, e.g. "404" or "500". Optional. */
  code?: string
  /** Primary heading, e.g. "This page doesn't exist." */
  title: string
  /** Optional supporting sentence below the title. */
  message?: string
  /** Sets document.title while mounted. */
  documentTitle?: string
  /** Add a `noindex` robots directive while mounted (default: true). */
  noindex?: boolean
  /** Solid backdrop behind the runes so they always read. */
  background?: string
  /**
   * Action element (rendered below the message). Defaults to a "Back to home"
   * CTA. Pass `null` to render no action.
   */
  action?: ReactNode
}

/**
 * Reusable error-page template built on the falling-rune animation
 * ({@link FallingLogos}). Use it for 404s, thrown route errors, or any
 * full-screen "something went wrong" state.
 *
 * Layout mirrors the contact page: the rune canvas sits behind the content,
 * draggable in the empty areas, while the heading/CTA stay interactive.
 */
export function ErrorPage({
  code,
  title,
  message,
  documentTitle,
  noindex = true,
  background = COLORS.neutral8,
  action,
}: ErrorPageProps) {
  // Report to the root layout so it hides the global footer and applies the
  // error title. The root is the single writer of document.title — setting it
  // here would be overwritten by the root's pathname-driven meta effect.
  useEffect(() => {
    setErrorPage({ active: true, title: documentTitle })
    return () => setErrorPage({ active: false })
  }, [documentTitle])

  // Robots noindex isn't managed by the root meta effect, so handle it here.
  useEffect(() => {
    if (!noindex) return
    const meta = document.querySelector('meta[name="robots"]')
    const original = meta?.getAttribute('content') ?? null
    meta?.setAttribute('content', 'noindex')
    return () => {
      if (meta && original !== null) meta.setAttribute('content', original)
    }
  }, [noindex])

  return (
    <>
      {/* Solid backdrop so the green runes read on any route's background. */}
      <div
        aria-hidden="true"
        style={{ position: 'fixed', inset: 0, zIndex: 0, background, pointerEvents: 'none' }}
      />

      {/* Rune animation */}
      <FallingLogos />

      {/* Content — pointer events pass through to the canvas except on the CTA. */}
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          padding: '0 30px',
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE_OUT }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          {code && (
            <span
              style={{
                fontFamily: BODY_FONT,
                fontSize: 'var(--text-6xl)',
                fontWeight: 300,
                lineHeight: 1,
                color: COLORS.green1,
              }}
            >
              {code}
            </span>
          )}
          <h1
            style={{
              fontFamily: BODY_FONT,
              fontSize: 'var(--text-3xl)',
              fontWeight: 300,
              color: COLORS.neutral2,
              margin: code ? '20px 0 0' : 0,
            }}
          >
            {title}
          </h1>
          {message && (
            <p
              style={{
                fontFamily: BODY_FONT,
                fontSize: 'var(--text-base)',
                fontWeight: 400,
                color: COLORS.neutral4,
                margin: '12px 0 0',
                maxWidth: '420px',
              }}
            >
              {message}
            </p>
          )}
          {action !== null && (
            <div style={{ marginTop: '32px', pointerEvents: 'auto' }}>
              {action ?? (
                <CtaButton label="Back to home" to="/" variant="outline" icon="arrow" />
              )}
            </div>
          )}
        </motion.div>
      </div>
    </>
  )
}
